// src/lib/learning/levelVideoFlow.js

import { supabase } from "../../supabaseClient";

const VIDEO_STEP_SOURCE = "supabase_level_videos";

function normalizeVideoLevelKey(levelKey) {
  if (!levelKey) return "";

  const text = String(levelKey).trim();

  if (/^L\d+$/i.test(text)) {
    return text.toUpperCase();
  }

  if (/^\d+$/.test(text)) {
    return `L${text}`;
  }

  const match = text.match(/(\d+)/);

  return match ? `L${match[1]}` : text.toUpperCase();
}

function normalizeVideoType(videoType) {
  const type = String(videoType || "").trim().toLowerCase();

  if (type === "intro") return "intro";
  if (type === "summary") return "summary";

  if (
    type === "knowledge_check" ||
    type === "knowledge-check" ||
    type === "concept" ||
    type === "concept_video"
  ) {
    return "knowledge_check";
  }

  return type || "video";
}

function isWelcomeStep(step) {
  return step?._type === "welcomeStep" || step?._type === "overviewDraftStep";
}

function isScenarioStep(step) {
  return (
    step?._type === "scenarioSelectStep" ||
    step?._type === "scenarioDragStep"
  );
}

function isActionStep(step) {
  return (
    step?._type === "practicalStep" ||
    step?._type === "practicalActionDraftStep" ||
    step?._type === "actionQuestionDraftStep" ||
    step?._type === "practicalActionCompleteDraftStep"
  );
}

function isResultStep(step) {
  return step?._type === "resultStep";
}

function resolveVideoUrl(row) {
  if (row?.video_url) {
    return row.video_url;
  }

  if (row?.storage_bucket && row?.storage_path) {
    const { data } = supabase.storage
      .from(row.storage_bucket)
      .getPublicUrl(row.storage_path);

    return data?.publicUrl || "";
  }

  return "";
}

function getVideoPlacement(row) {
  const videoType = normalizeVideoType(row?.video_type);

  if (videoType === "knowledge_check") {
    return `knowledge-check-${row?.knowledge_check_number || "unknown"}`;
  }

  return videoType;
}

function getVideoTitle(row) {
  const videoType = normalizeVideoType(row?.video_type);

  if (row?.title) return row.title;
  if (videoType === "intro") return "Intro Video";
  if (videoType === "summary") return "Summary Video";

  if (videoType === "knowledge_check") {
    const number = row?.knowledge_check_number || "";
    return `Concept ${number} Video`.trim();
  }

  return "Video";
}

function makeVideoStep(row) {
  const videoType = normalizeVideoType(row?.video_type);
  const placement = getVideoPlacement(row);
  const title = getVideoTitle(row);
  const videoUrl = resolveVideoUrl(row);

  return {
    _type: "videoStep",
    _key: `video-${row?.id || placement}`,

    internalTitle: `video-${placement}`,
    displayTitle: title,
    title,

    videoUrl,
    videoType,
    knowledgeCheckNumber: row?.knowledge_check_number || null,
    durationSeconds: row?.duration_seconds || null,

    nextLabel: "NEXT",

    source: VIDEO_STEP_SOURCE,
    sourceVideoId: row?.id || null,
  };
}

function removeSupabaseVideoSteps(steps = []) {
  return steps.filter((step) => {
    return !(
      step?._type === "videoStep" &&
      step?.source === VIDEO_STEP_SOURCE
    );
  });
}

function getRowRank(row) {
  const videoType = normalizeVideoType(row?.video_type);

  if (videoType === "intro") return 1;
  if (videoType === "knowledge_check") return 2;
  if (videoType === "summary") return 3;

  return 4;
}

function sortVideoRows(rows = []) {
  return [...rows].sort((a, b) => {
    const rankDiff = getRowRank(a) - getRowRank(b);

    if (rankDiff !== 0) {
      return rankDiff;
    }

    const aNumber = Number(a?.knowledge_check_number || 0);
    const bNumber = Number(b?.knowledge_check_number || 0);

    return aNumber - bNumber;
  });
}

function groupVideos(rows = []) {
  const grouped = {
    intro: null,
    summary: null,
    knowledgeChecks: new Map(),
  };

  sortVideoRows(rows).forEach((row) => {
    const videoUrl = resolveVideoUrl(row);

    if (!videoUrl) {
      return;
    }

    const videoType = normalizeVideoType(row?.video_type);

    if (videoType === "intro") {
      if (!grouped.intro) grouped.intro = row;
      return;
    }

    if (videoType === "summary") {
      if (!grouped.summary) grouped.summary = row;
      return;
    }

    if (videoType === "knowledge_check") {
      const knowledgeCheckNumber = Number(row?.knowledge_check_number);

      if (!Number.isFinite(knowledgeCheckNumber) || knowledgeCheckNumber <= 0) {
        return;
      }

      if (!grouped.knowledgeChecks.has(knowledgeCheckNumber)) {
        grouped.knowledgeChecks.set(knowledgeCheckNumber, row);
      }
    }
  });

  return grouped;
}

function insertBeforeFirstActionOrResult(steps = [], videoStep) {
  if (!videoStep) {
    return steps;
  }

  const insertIndex = steps.findIndex((step) => {
    return isActionStep(step) || isResultStep(step);
  });

  if (insertIndex < 0) {
    return [...steps, videoStep];
  }

  return [
    ...steps.slice(0, insertIndex),
    videoStep,
    ...steps.slice(insertIndex),
  ];
}

function insertVideosIntoSteps(steps = [], groupedVideos) {
  const cleanSteps = removeSupabaseVideoSteps(steps);
  const output = [];

  let introInserted = false;

  cleanSteps.forEach((step) => {
    output.push(step);

    if (!introInserted && groupedVideos?.intro && isWelcomeStep(step)) {
      output.push(makeVideoStep(groupedVideos.intro));
      introInserted = true;
      return;
    }

    if (isScenarioStep(step)) {
      const knowledgeCheckNumber = Number(step?.knowledgeCheckNumber || 0);

      if (!Number.isFinite(knowledgeCheckNumber) || knowledgeCheckNumber <= 0) {
        return;
      }

      const matchingVideo =
        groupedVideos?.knowledgeChecks?.get(knowledgeCheckNumber);

      if (matchingVideo) {
        output.push(makeVideoStep(matchingVideo));
      }
    }
  });

  if (groupedVideos?.summary) {
    return insertBeforeFirstActionOrResult(
      output,
      makeVideoStep(groupedVideos.summary)
    );
  }

  return output;
}

export async function fetchLevelVideosForLevel(levelKey) {
  const normalizedLevelKey = normalizeVideoLevelKey(levelKey);

  if (!normalizedLevelKey) {
    return [];
  }

  const { data, error } = await supabase
    .from("level_videos")
    .select(
      `
        id,
        level_key,
        video_type,
        knowledge_check_number,
        title,
        video_url,
        storage_bucket,
        storage_path,
        duration_seconds,
        is_active
      `
    )
    .eq("is_active", true)
    .ilike("level_key", normalizedLevelKey)
    .order("video_type", { ascending: true })
    .order("knowledge_check_number", { ascending: true });

  if (error) {
    console.warn("[levelVideoFlow] Could not load level videos:", error);
    return [];
  }

  return sortVideoRows(Array.isArray(data) ? data : []);
}

export async function attachLevelVideosToRuntimeLevel(level) {
  if (!level?.steps?.length) {
    return level;
  }

  const normalizedLevelKey = normalizeVideoLevelKey(
    level.levelKey || level.legacyLevelKey || level.levelOrder
  );

  if (!normalizedLevelKey) {
    return level;
  }

  const videos = await fetchLevelVideosForLevel(normalizedLevelKey);

  if (!videos.length) {
    return {
      ...level,
      steps: removeSupabaseVideoSteps(level.steps),
    };
  }

  const groupedVideos = groupVideos(videos);

  return {
    ...level,
    steps: insertVideosIntoSteps(level.steps, groupedVideos),
  };
}

export default attachLevelVideosToRuntimeLevel;